import React, { useLayoutEffect, useRef } from 'react';
import { Box, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { buildSummaryUI } from './SummaryUI';
import { itemsFor as qItemsFor, getAllKeysForType, getAllIconKeys as qGetAllIconKeys } from './QuestionUtils';
import { getDimOverlayStyle } from './GameTheme';
import { QUESTIONS_PER_RUN } from './GameConfig';
import BackToGames from './BackToGames';
import { useGameAccessGuard } from './useGameAccessGuard';
import GameLoadingScreen from './GameLoadingScreen';

// Game 8 — Category Guess (MCQ)
// Show 2–3 items. Player chooses the correct Function, Feature, or Class from 3 options.
// - 20 rounds
// - Options come from the same type as the target (to avoid ambiguity)
// - Voice-over prompt + sfx. Optional speech recognition to pick options by voice.
// - Shuffle button

const ENABLE_SPEECH = false; // toggle if needed

function shuffle(arr) { const a = arr.slice(); for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; }
const choice = (arr) => arr[Math.floor(Math.random() * arr.length)];

// Build a pool of all asset keys used across mappings (for preloading)
function getAllIconKeys() {
  // Delegate to QuestionUtils to keep a single source of truth
  return qGetAllIconKeys();
}

// Local wrapper for clarity in this file
function itemsFor(type, key) {
  return qItemsFor(type, key);
}

function displayNameForOption(type, key) {
  // Keys are human-readable already in our mappings
  return key;
}

function buildRoundFrom(type, key) {
  const pool = itemsFor(type, key);
  if (!pool || pool.length < 3) return null;
  // Allow 2–4 exemplars depending on availability
  const candidateCounts = [2,3,4].filter(c => pool.length >= c);
  const count = candidateCounts.length ? choice(candidateCounts) : 2;
  const picks = shuffle(pool).slice(0, count);

  // Distractors: two other options of the same type that share none of the picked items
  const allKeys = getAllKeysForType(type).filter(k => k !== key);
  const exclude = new Set(picks);
  const compatible = allKeys.filter(k => (itemsFor(type, k) || []).every(img => !exclude.has(img)));
  if (compatible.length < 2) return null;
  const distractors = shuffle(compatible).slice(0, 2);
  const options = shuffle([key, ...distractors]).map(k => ({ type, key: k, label: displayNameForOption(type, k) }));

  const prompt = type === 'function' ? 'Which action fits these items?' : type === 'feature' ? 'Which feature fits these items?' : 'These are all part of which group?';

  return { type, key, picks, options, correctLabel: displayNameForOption(type, key), prompt };
}

function buildRounds(limit = 20) {
  // Build a unique pool of (type,key) across all types and pick rounds without repeats
  const types = ['function', 'feature', 'class'];
  const builders = [];
  types.forEach(t => {
    try { (getAllKeysForType(t) || []).forEach(k => builders.push({ type: t, key: k })); } catch {}
  });
  const order = shuffle(builders);
  const rounds = [];
  for (let i = 0; i < order.length && rounds.length < limit; i++) {
    const b = order[i];
    const r = buildRoundFrom(b.type, b.key);
    if (r) rounds.push(r);
  }
  return rounds.slice(0, limit);
}

const GameContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  minHeight: '70vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  background:
    'linear-gradient(135deg, rgba(4, 37, 57, 0.1) 0%, rgba(87, 199, 133, 0.05) 50%, rgba(249, 117, 68, 0.05) 100%)',
}));

const GameBoard = styled(Paper)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: '1000px',
  minWidth: '280px',
  aspectRatio: '4/3',
  backgroundImage: 'url(/Games/backgrounds/school.jpg)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
  border: '1px solid rgba(255,255,255,0.2)',
  backdropFilter: 'blur(10px)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  ...getDimOverlayStyle(),
}));

export function Game8() {
  const { allowed, checking } = useGameAccessGuard(1);
  const containerRef = useRef();
  const phaserRef = useRef();
  const resizeObserverRef = useRef();

  useLayoutEffect(() => {
    // If still checking, or not allowed, ensure any existing instance is torn down and exit.
    if (checking) return; // wait until guard resolved
    if (!allowed) {
      try { resizeObserverRef.current?.disconnect(); } catch {}
      try { phaserRef.current?.destroy(true); } catch {}
      return; // do not initialize Phaser
    }
    let mounted = true;
    const container = containerRef.current;
    if (!container) return;

    import('phaser').then((PhaserLib) => {
      if (!mounted || !container) return;
      const PhaserGame = PhaserLib.default;
      phaserRef.current?.destroy(true);

      class PreloadScene extends PhaserGame.Scene {
        constructor() { super({ key: 'PreloadScene' }); }
        preload() {
          const W = this.scale.width; const H = this.scale.height;
          const COLOR_DARK = 0x042539; const COLOR_ACCENT = 0x57c785; const COLOR_ACCENT_2 = 0xf9644d;

          const dpr = Math.max(1, window.devicePixelRatio || 1);

          const panel = this.add.graphics();
          const panelW = Math.min(W * 0.7, 560 * dpr); const panelH = Math.min(H * 0.24, 180 * dpr);
          panel.fillStyle(0xffffff, 0.12);
          panel.fillRoundedRect((W - panelW) / 2, (H - panelH) / 2, panelW, panelH, 18 * dpr);
          panel.lineStyle(4 * dpr, COLOR_DARK, 1);
          panel.strokeRoundedRect((W - panelW) / 2, (H - panelH) / 2, panelW, panelH, 18 * dpr);

          const loadingFontCss = Math.max(18, Math.min(36, (H / dpr) * 0.055));
          const loadingText = this.add.text(W / 2, H / 2 - panelH * 0.28, 'Loading...', {
            fontFamily: 'Fredoka One',
            fontSize: `${Math.round(loadingFontCss * dpr)}px`,
            color: '#ffffff', stroke: '#1e607d', strokeThickness: Math.round(3 * dpr), align: 'center',
          }).setOrigin(0.5);
          try { loadingText.setResolution(dpr); } catch {}

          const barW = Math.min(panelW * 0.78, 600 * dpr); const barH = Math.max(12 * dpr, Math.min(18 * dpr, H * 0.025));
          const barX = (W - barW) / 2; const barY = H / 2 + barH * 0.5;
          const barBg = this.add.graphics(); barBg.fillStyle(0xffffff, 0.25);
          barBg.fillRoundedRect(barX, barY, barW, barH, 10 * dpr);
          barBg.lineStyle(3 * dpr, COLOR_DARK, 1); barBg.strokeRoundedRect(barX, barY, barW, barH, 10 * dpr);
          const barFill = this.add.graphics();
          const percentFontCss = Math.max(14, Math.min(22, (H / dpr) * 0.035));
          const percentText = this.add.text(W / 2, barY + barH * 2, '0%', {
            fontFamily: 'Fredoka One', fontSize: `${Math.round(percentFontCss * dpr)}px`, color: '#ffffff', stroke: '#1e607d', strokeThickness: Math.round(2 * dpr),
          }).setOrigin(0.5);
          try { percentText.setResolution(dpr); } catch {}

          this.load.script('webfont', 'https://ajax.googleapis.com/ajax/libs/webfont/1.6.26/webfont.js');
          // Use shared preloader list to ensure all icons referenced by QuestionUtils are loaded
          getAllIconKeys().forEach((key) => this.load.image(key, `/Games/icons/${encodeURIComponent(key)}.png`));

          this.prog = { v: 0 };
          const drawBar = (p) => {
            const w = Math.max(0, Math.min(1, p)) * barW; barFill.clear();
            barFill.fillStyle(COLOR_ACCENT, 0.95); barFill.fillRoundedRect(barX, barY, w, barH, 10);
            barFill.lineStyle(2, COLOR_DARK, 1); barFill.strokeRoundedRect(barX, barY, Math.max(w, 2), barH, 10);
            if (w > 8) {
              const t = (this.time.now % 1200) / 1200; const sheenWidth = Math.max(30, Math.min(80, w * 0.25));
              const sxRaw = barX - 60 + (w + 120) * t; const startX = Math.max(barX, sxRaw); const endX = Math.min(barX + w, sxRaw + sheenWidth); const cw = endX - startX;
              if (cw > 0) {
                barFill.fillStyle(0xffffff, 0.2);
                barFill.beginPath(); barFill.moveTo(startX, barY); barFill.lineTo(startX + Math.min(16, cw * 0.25), barY);
                barFill.lineTo(startX + cw, barY + barH); barFill.lineTo(startX + Math.max(0, cw - Math.min(16, cw * 0.25)), barY + barH);
                barFill.closePath(); barFill.fillPath();
                barFill.fillStyle(0xf9644d, 0.12); barFill.fillRect(Math.max(barX, endX - 2), barY + 2, 2, barH - 4);
              }
            }
          };
          drawBar(0);
          this.sheenTick = this.time.addEvent({ delay: 40, loop: true, callback: () => drawBar(this.prog.v) });

          this.load.on('progress', (value) => {
            if (this.progressTween) this.progressTween.remove();
            const start = this.prog.v; const dist = Math.abs(value - start);
            this.progressTween = this.tweens.add({ targets: this.prog, v: value, duration: Math.max(200, Math.min(700, 800 * dist)), ease: 'Sine.easeOut', onUpdate: () => {
              drawBar(this.prog.v); percentText.setText(`${Math.round(this.prog.v * 100)}%`);
            }});
          });

          this.load.once('complete', () => {
            const scene = this;
            const WF = window.WebFont;
            if (WF && WF.load) {
              WF.load({ google: { families: ['Fredoka One'] }, active: () => { scene.sheenTick?.remove(); scene.scene.start('CategoryMCQScene'); }, inactive: () => { scene.sheenTick?.remove(); scene.scene.start('CategoryMCQScene'); } });
            } else {
              scene.sheenTick?.remove();
              scene.scene.start('CategoryMCQScene');
            }
          });
        }
      }

      class CategoryMCQScene extends PhaserGame.Scene {
        constructor() { super({ key: 'CategoryMCQScene' }); }
        init() {
          this.rounds = buildRounds(QUESTIONS_PER_RUN);
          this.roundIndex = 0;
          this.perRoundMistake = false;
          this.perfectRounds = 0;
          this.correctAudioFiles = [ '/Games/audio/right1.mp3', '/Games/audio/right2.mp3', '/Games/audio/right3.mp3' ];
          this.wrongAudioFiles = [ '/Games/audio/wrong1.mp3', '/Games/audio/wrong2.mp3' ];
          this.itemSprites = [];
          this.optionButtons = [];
          // Guard: prevent multiple scoring per round
          this.roundLocked = false;
        }
        // DPR helpers
        getDpr() { return Math.max(1, window.devicePixelRatio || 1); }
        px(n) { return Math.round(n * this.getDpr()); }
        preload() {}
        create() {
          const dpr = this.getDpr();
          const hCss = this.scale.height / dpr;
          // Prompt & progress
          const qFontCss = Math.max(18, Math.min(40, hCss * 0.075));
          this.promptText = this.add.text(this.scale.width / 2, this.px(10), '', {
            fontFamily: 'Fredoka One', fontSize: `${Math.round(qFontCss * dpr)}px`, color: '#ffffff', stroke: '#042539', strokeThickness: this.px(6), align: 'center', wordWrap: { width: this.scale.width - this.px(40) },
          }).setOrigin(0.5).setShadow(2 * dpr, 2 * dpr, 'rgba(0,0,0,0.4)', 6 * dpr);
          try { this.promptText.setResolution(dpr); } catch {}

          const pFontCss = Math.max(14, Math.min(28, hCss * 0.05));
          this.progressText = this.add.text(this.scale.width / 12, this.px(10), '', {
            fontFamily: 'Fredoka One', fontSize: `${Math.round(pFontCss * dpr)}px`, color: '#ffffff', stroke: '#042539', strokeThickness: this.px(5),
          }).setOrigin(0.5).setShadow(2 * dpr, 2 * dpr, 'rgba(0,0,0,0.4)', 6 * dpr);
          try { this.progressText.setResolution(dpr); } catch {}

          this.createShuffleButton();
          this.positionShuffleButton();

          // Keep progress at the same height as Shuffle
          this.positionTopBar = () => {
            if (this.progressText && this.shuffleBtn) {
              this.progressText.y = this.shuffleBtn.y;
            }
          };
          this.positionTopBar();

          // Compute safe top area and position prompt below it
          this.positionPrompt = () => {
            if (!this.promptText) return;
            const bottoms = [
              this.progressText ? (this.progressText.y + this.progressText.height / 2) : 0,
              (this.shuffleBtn && this.shuffleBtn.meta) ? (this.shuffleBtn.y + this.shuffleBtn.meta.height / 2) : 0,
              (this.micBtn && this.micBtn.meta) ? (this.micBtn.y + this.micBtn.meta.h / 2) : 0,
            ];
            const topSafe = Math.max(...bottoms);
            const pad = this.px(4); // 50% tighter than before so prompt sits higher
            this.promptText.y = topSafe + pad + this.promptText.height / 2;
            try { this.promptText.setStyle({ wordWrap: { width: this.scale.width - this.px(40) } }); } catch {}
          };
          this.positionPrompt();

          // Guarded resize handler
          this.onResizeHandler = () => {
            if (!this.sys || this.sys.isDestroyed || !this.scale || !this.game) return;
            try { this.positionShuffleButton && this.positionShuffleButton(); } catch {}
            try { this.positionTopBar && this.positionTopBar(); } catch {}
            try { this.positionPrompt && this.positionPrompt(); } catch {}
            try { typeof this.layoutOptionButtons === 'function' && this.layoutOptionButtons(); } catch {}
            try { typeof this.layoutItems === 'function' && this.layoutItems(); } catch {}
          };
          this.scale.on('resize', this.onResizeHandler, this);
          const off = () => { try { this.scale?.off('resize', this.onResizeHandler, this); } catch {} };
          this.events.once('shutdown', off);
          this.events.once('destroy', off);

          this.buildMicButton();
          // Reposition prompt and top bar in case mic affected layout
          this.positionTopBar && this.positionTopBar();
          this.positionPrompt && this.positionPrompt();

          this.showRound();
        }
        createShuffleButton() {
          const dpr = this.getDpr();
          const hCss = this.scale.height / dpr;
          const sFontSizeCss = Math.max(14, Math.min(24, hCss * 0.045));
          const text = this.add.text(0, 0, 'Shuffle', { fontFamily: 'Fredoka One', fontSize: `${Math.round(sFontSizeCss * dpr)}px`, color: '#042539' }).setOrigin(0.5);
          try { text.setResolution(dpr); } catch {}
          const width = text.width + this.px(32); const height = text.height + this.px(16);
          const bg = this.add.graphics(); bg.fillStyle(0xffffff, 0.6); bg.fillRoundedRect(-width / 2, -height / 2, width, height, this.px(12)); bg.lineStyle(this.px(4), 0x042539, 1); bg.strokeRoundedRect(-width / 2, -height / 2, width, height, this.px(12));
          const x = this.scale.width - this.px(20) - width / 2; const y = Math.max(this.px(10) + height / 2, this.px(hCss * 0.04) + height / 2);
          const container = this.add.container(x, y, [bg, text]); container.setSize(width, height); container.setInteractive({ useHandCursor: true });
          container.on('pointerover', () => { this.tweens.add({ targets: container, scale: 1.05, duration: 200 }); bg.clear(); bg.fillStyle(0x57C785, 0.8); bg.fillRoundedRect(-width / 2, -height / 2, width, height, this.px(12)); bg.lineStyle(this.px(6), 0x042539, 1); bg.strokeRoundedRect(-width / 2, -height / 2, width, height, this.px(12)); text.setColor('#ffffff'); });
          container.on('pointerout', () => { this.tweens.add({ targets: container, scale: 1, duration: 200 }); bg.clear(); bg.fillStyle(0xffffff, 0.6); bg.fillRoundedRect(-width / 2, -height / 2, width, height, this.px(12)); bg.lineStyle(this.px(4), 0x042539, 1); bg.strokeRoundedRect(-width / 2, -height / 2, width, height, this.px(12)); text.setColor('#042539'); });
          container.on('pointerdown', () => { this.tweens.add({ targets: container, scale: 1.1, duration: 300, ease: 'Circ.easeInOut', onComplete: () => this.handleShuffle() }); });
          container.meta = { width, height }; this.shuffleBtn = container;
        }
        positionShuffleButton() { if (!this.shuffleBtn) return; const width = this.shuffleBtn.meta.width; const height = this.shuffleBtn.meta.height; const dpr = this.getDpr(); const hCss = this.scale.height / dpr; this.shuffleBtn.x = this.scale.width - this.px(20) - width / 2; this.shuffleBtn.y = Math.max(this.px(10) + height / 2, this.px(hCss * 0.04) + height / 2); }
        handleShuffle() { this.rounds = buildRounds(QUESTIONS_PER_RUN); this.roundIndex = 0; this.perRoundMistake = false; this.perfectRounds = 0; this.roundLocked = false; this.showRound(); }

        buildMicButton() {
          if (!ENABLE_SPEECH) return;
          const dpr = this.getDpr();
          const hCss = this.scale.height / dpr;
          const sCss = Math.max(24, hCss * 0.035);
          const icon = this.add.text(0, 0, '🎤', { fontSize: `${Math.round(sCss * dpr)}px` }).setOrigin(0.5);
          try { icon.setResolution(dpr); } catch {}
          const w = Math.round(sCss * dpr) + this.px(18); const h = Math.round(sCss * dpr) + this.px(12);
          const bg = this.add.graphics();
          bg.fillStyle(0xffffff, 0.6); bg.fillRoundedRect(-w/2, -h/2, w, h, this.px(10)); bg.lineStyle(this.px(4), 0x042539, 1); bg.strokeRoundedRect(-w/2, -h/2, w, h, this.px(10));
          const ct = this.add.container(this.scale.width * 0.08, Math.max(this.px(24), this.px(hCss * 0.08)), [bg, icon]);
          ct.setSize(w, h); ct.setInteractive({ useHandCursor: true });
          ct.on('pointerover', () => { this.tweens.add({ targets: ct, scale: 1.05, duration: 160 }); });
          ct.on('pointerout', () => { this.tweens.add({ targets: ct, scale: 1.0, duration: 160 }); });
          ct.on('pointerdown', () => this.startSpeechRecognition());
          this.micBtn = ct; this.micBtn.meta = { w, h };
        }

        showRound() {
          if (this.roundIndex >= this.rounds.length) {
            // Save score to database instead of localStorage
            import('Utils/ProgressTracker').then(({ recordSession }) => {
              recordSession(8, this.perfectRounds).catch(error => {
                console.error('Error saving score for Game 8:', error);
              });
            });
            this.scene.start('SummaryScene', { correct: this.perfectRounds, total: this.rounds.length }); return;
          }
          // Reset round lock
          this.roundLocked = false;
          this.perRoundMistake = false;

          // Clear UI
          this.itemSprites.forEach(s => { try { s.destroy(); } catch {} }); this.itemSprites = [];
          this.optionButtons.forEach(b => { try { b.destroy(); } catch {} }); this.optionButtons = [];

          const round = this.rounds[this.roundIndex];
          this.promptText.setText(round.prompt);
          this.progressText.setText(`${this.roundIndex + 1} / ${this.rounds.length}`);
          this.positionPrompt && this.positionPrompt();
          this.speak(round.prompt);

          // Layout 2-3 items in a row
          const count = round.picks.length;
          const areaW = this.scale.width * 0.85;
          const imgSize = Math.min(this.scale.width, this.scale.height) * (count === 2 ? 0.22 : 0.2);
          const centerY = this.scale.height * 0.36;
          const startX = this.scale.width / 2 - ((count - 1) * (areaW / count)) / 2;
          round.picks.forEach((key, idx) => {
            const x = startX + idx * (areaW / count);
            const sp = this.add.image(x, centerY, key).setDisplaySize(imgSize, imgSize).setOrigin(0.5);
            this.tweens.add({ targets: sp, y: '+=6', duration: 1400, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
            this.itemSprites.push(sp);
          });
          // Options row: fixed width = 1/4 container width (DPR aware) to avoid overlap
          const options = round.options;
          const oAreaW = this.scale.width * 0.9; // for centering only
          const colGap = this.px(16);
          const perWidth = Math.max(this.px(60), Math.floor(this.scale.width * 0.25) - colGap);
          const optY = this.scale.height * 0.77;
          const oStartX = this.scale.width / 2 - ((options.length - 1) * (oAreaW / options.length)) / 2;

          options.forEach((opt, idx) => {
            const colCenter = oStartX + idx * (oAreaW / options.length);
            const btn = this.createTextButton(colCenter, optY, opt.label, perWidth);
            btn.meta = btn.meta || {};
            btn.meta.addPointerDown(() => {
              if (this.roundLocked) return;
              this.handleAnswer(opt.label, round.correctLabel, btn);
            });
            this.optionButtons.push(btn);
          });

          this.layoutItems = () => {
            const dpr2 = window.devicePixelRatio || 1;
            const cssW2 = this.scale.width / dpr2; const cssH2 = this.scale.height / dpr2;
            const count2 = round.picks.length; const areaW2 = this.scale.width * 0.85; const imgCssSize2 = Math.min(cssW2, cssH2) * (count2 === 2 ? 0.22 : 0.2); const imgSize2 = imgCssSize2 * dpr2; const centerY2 = this.scale.height * 0.36; const startX2 = this.scale.width / 2 - ((count2 - 1) * (areaW2 / count2)) / 2;
            if (!this.itemSprites || this.itemSprites.length === 0) return;
            this.itemSprites.forEach((sp, i) => { if (!sp || !sp.scene || sp._destroyed) return; sp.setDisplaySize(imgSize2, imgSize2); sp.x = startX2 + i * (areaW2 / count2); sp.y = centerY2; });
          };

          this.layoutOptionButtons = () => {
            if (!options || !this.optionButtons || this.optionButtons.length === 0) return;
            const oAreaW2 = this.scale.width * 0.9;
            const perWidth2 = Math.max(this.px(60), Math.floor(this.scale.width * 0.25) - this.px(16));
            const optY2 = this.scale.height * 0.77;
            const oStartX2 = this.scale.width / 2 - ((options.length - 1) * (oAreaW2 / options.length)) / 2;
            this.optionButtons.forEach((btn, i) => { if (!btn || !btn.scene || btn._destroyed) return; btn.x = oStartX2 + i * (oAreaW2 / options.length); btn.y = optY2; try { btn.meta?.resize?.(perWidth2); } catch {} });
          };
        }

        handleAnswer(label, correctLabel, btn) {
          // Early exit if round is locked
          if (this.roundLocked) return;
          if (label === correctLabel) {
            this.roundLocked = true; // lock immediately on correct
            new Audio(PhaserGame.Utils.Array.GetRandom(this.correctAudioFiles)).play();
            if (!this.perRoundMistake) this.perfectRounds += 1;
            this.tweens.add({ targets: btn, scale: 1.15, duration: 220, ease: 'Circ.easeOut', yoyo: true });
            this.speak('Great job!');
            this.time.delayedCall(500, () => { this.roundIndex += 1; this.showRound(); });
          } else {
            new Audio(PhaserGame.Utils.Array.GetRandom(this.wrongAudioFiles)).play();
            this.perRoundMistake = true;
            this.tweens.add({ targets: btn, x: '+=6', duration: 60, yoyo: true, repeat: 2 });
          }
        }

        createTextButton(x, y, label, maxWidth) {
          const dpr = this.getDpr();
          const clampW = (w) => Math.max(this.px(60), Math.min(w, this.px(420)));
          const targetWidth = clampW(typeof maxWidth === 'number' ? maxWidth : this.px(260));
          const fsCssBase = Math.max(18, Math.min(28, (this.scale.height / dpr) * 0.045));
          const text = this.add.text(0, 0, label, {
            fontFamily: 'Fredoka One',
            fontSize: `${Math.round(fsCssBase * dpr)}px`,
            color: '#042539',
            align: 'center',
          }).setOrigin(0.5, 0.5);
          try { text.setResolution(dpr); } catch {}

          const padX = this.px(40);

          const setFsCss = (cssPx) => {
            text.setStyle({ fontSize: `${Math.round(cssPx * dpr)}px` });
            try { text.setResolution(dpr); } catch {}
            text.setText(text.text);
          };
          const shrinkToFit = (maxW) => {
            const minCss = 12;
            let css = fsCssBase;
            // Disable wrapping for measurement
            text.setStyle({ wordWrap: { width: Math.max(maxW * 4, this.px(4000)), useAdvancedWrap: true } });
            setFsCss(css);
            let safety = 40;
            while (text.width > maxW - padX && css > minCss && safety-- > 0) {
              const factor = Math.max(0.8, (maxW - padX) / Math.max(1, text.width));
              css = Math.max(minCss, Math.floor(css * factor));
              setFsCss(css);
            }
            // Keep wrapping effectively disabled so text never wraps
            text.setStyle({ wordWrap: { width: Math.max(maxW * 4, this.px(4000)), useAdvancedWrap: true } });
            text.setText(text.text);
            return css;
          };

          // Initial shrink-to-fit
          const finalCss = shrinkToFit(targetWidth);

          let width = targetWidth;
          let height = Math.max(this.px(44), text.height + this.px(18));

          const bg = this.add.graphics();
          const drawDefault = () => {
            bg.clear();
            bg.fillStyle(0xffffff, 0.8);
            bg.fillRoundedRect(-width / 2, -height / 2, width, height, this.px(14));
            bg.lineStyle(this.px(4), 0x042539, 1);
            bg.strokeRoundedRect(-width / 2, -height / 2, width, height, this.px(14));
          };
          const drawHover = () => {
            bg.clear();
            bg.fillStyle(0x57C785, 0.8);
            bg.fillRoundedRect(-width / 2, -height / 2, width, height, this.px(14));
            bg.lineStyle(this.px(4), 0x042539, 1);
            bg.strokeRoundedRect(-width / 2, -height / 2, width, height, this.px(14));
          };
          drawDefault();

          const ct = this.add.container(x, y, [bg, text]);
          ct.setSize(width, height);

          const hit = this.add.zone(0, 0, width, height).setOrigin(0.5);
          ct.add(hit);
          hit.setInteractive({ cursor: 'pointer' });

          // Track destruction to guard later resizes
          ct._destroyed = false; text._destroyed = false;
          ct.once('destroy', () => { ct._destroyed = true; text._destroyed = true; });

          let isHover = false;
          let scaleTween = null;

          const hoverIn = () => { if (isHover || ct._destroyed || text._destroyed) return; isHover = true; if (scaleTween) scaleTween.stop(); drawHover(); scaleTween = this.tweens.add({ targets: text, scale: 1.1, duration: 200 }); };
          const hoverOut = () => { if (!isHover) return; isHover = false; if (ct._destroyed || text._destroyed) return; if (scaleTween) scaleTween.stop(); scaleTween = this.tweens.add({ targets: text, scale: 1.0, duration: 200 }); drawDefault(); };

          hit.on('pointerover', hoverIn);
          hit.on('pointerout', hoverOut);

          ct.meta = {
            addPointerDown: (cb) => { hit.on('pointerdown', cb); },
            resize: (newMaxWidth) => {
              if (!ct || !ct.scene || !text || !text.scene || ct._destroyed || text._destroyed) return;
              const newW = clampW(typeof newMaxWidth === 'number' ? newMaxWidth : this.px(260));
              if (newW === width) return; // avoid unnecessary reflow
              // re-run shrink-to-fit with new width
              shrinkToFit(newW);
              const newH = Math.max(this.px(44), text.height + this.px(18));
              width = newW; height = newH;
              ct.setSize(newW, newH);
              drawDefault();
              if (isHover) drawHover();
              hit.setSize(newW, newH);
            },
          };
          return ct;
        }

        speak(text) { if (!ENABLE_SPEECH) return; try { const u = new SpeechSynthesisUtterance(text); u.rate = 0.95; u.pitch = 1.0; u.volume = 1.0; window.speechSynthesis?.speak(u); } catch {} }
        startSpeechRecognition() {
          if (!ENABLE_SPEECH) return; const WSR = window.SpeechRecognition || window.webkitSpeechRecognition; if (!WSR) return;
          try {
            const rec = new WSR(); rec.lang = 'en-US'; rec.maxAlternatives = 3; rec.interimResults = false; rec.continuous = false;
            rec.onresult = (e) => {
              const text = Array.from(e.results).map(r => r[0]?.transcript || '').join(' ').toLowerCase();
              // Try to match any current option label
              const round = this.rounds[this.roundIndex];
              if (!round) return;
              this.optionButtons.forEach((btn) => {
                try {
                  const label = btn.list?.find?.(c => c.style)?.text || '';
                  if (label && text.includes(label.toLowerCase())) { btn.emit?.('pointerdown'); }
                } catch {}
              });
            };
            rec.start();
          } catch {}
        }
      }

      class SummaryScene extends PhaserGame.Scene {
        constructor() { super({ key: 'SummaryScene' }); }
        async init(data) { 
          this.correct = data.correct || 0; 
          this.total = data.total || 0; 
          
          // Load history from database instead of localStorage
          try {
            const { getLastNSessions } = await import('Utils/ProgressTracker');
            this.history = await getLastNSessions(8, 20);
          } catch (e) {
            console.error('Error loading game history:', e);
            this.history = [];
          }
          this.localHistory = this.history.slice(-5); 
        }
        preload() { this.load.script('webfont', 'https://ajax.googleapis.com/ajax/libs/webfont/1.6.26/webfont.js'); }
        create() {
          const W = this.scale.width; const H = this.scale.height;
          const renderSummary = () => {
            buildSummaryUI(this, { correct: this.correct, total: this.total, history: this.localHistory, onRestart: () => { const fn = this.game?.reactHandleShuffle; if (typeof fn === 'function') { fn(); } else { this.scene.start('CategoryMCQScene'); } }, texts: { heading: `You got ${this.correct} correct on first try!`, playAgain: 'Play Again' }, graph: { x: W / 2, y: H / 2 + 150, width: 400, height: 250, titleText: 'Progress Over Past 5 Attempts', entrance: { fromYOffset: 300, delay: 200 } }, renderHeading: true });
          };
          const WF = window.WebFont;
          if (WF && WF.load) {
            WF.load({ google: { families: ['Fredoka One'] }, active: renderSummary, inactive: renderSummary });
          } else {
            renderSummary();
          }
        }
      }

      const ratio = window.devicePixelRatio || 1;
      const config = {
        type: PhaserGame.AUTO,
        parent: container,
        transparent: true,
        scene: [PreloadScene, CategoryMCQScene, SummaryScene],
        scale: { mode: PhaserGame.Scale.NONE, width: container.clientWidth * ratio, height: container.clientHeight * ratio },
        callbacks: { postBoot: (game) => { game.canvas.style.width = `${container.clientWidth}px`; game.canvas.style.height = `${container.clientHeight}px`; } },
      };

      phaserRef.current = new PhaserGame.Game(config);

      // Expose a regeneration hook for SummaryUI Play Again
      phaserRef.current.reactHandleShuffle = () => {
        try { phaserRef.current.scene.stop('SummaryScene'); } catch {}
        try { phaserRef.current.scene.stop('CategoryMCQScene'); } catch {}
        phaserRef.current.scene.start('CategoryMCQScene');
      };

      resizeObserverRef.current = new ResizeObserver(() => {
        if (!phaserRef.current) return;
        const w = container.clientWidth; const h = container.clientHeight;
        phaserRef.current.scale.resize(w * ratio, h * ratio);
        phaserRef.current.canvas.style.width = `${w}px`;
        phaserRef.current.canvas.style.height = `${h}px`;
      });
      resizeObserverRef.current.observe(container);
    });

    return () => {
      mounted = false;
      try { resizeObserverRef.current?.disconnect(); } catch {}
      try { phaserRef.current?.destroy(true); } catch {}
    };
  }, [checking, allowed]);

  // After hooks: handle conditional rendering
  if (checking) return <GameLoadingScreen />;
  if (!allowed) return null;

  return (
    <div>
      <GameContainer>
        <div className="pt-24 w-full flex justify-center items-center">
          <div style={{ width: '100%', maxWidth: 1100, margin: '0 auto', padding: '0 12px' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
              <BackToGames />
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: '100%', maxWidth: '1000px', minWidth: '280px' }}>
                <GameBoard ref={containerRef} />
              </div>
            </div>
          </div>
        </div>
      </GameContainer>
    </div>
  );
}

export default Game8;
